import React from 'react';
import { Table, TableRenderProps } from '@block-explorer/ui';
import { PaginationProps } from '../table-settings/table-settings';
import {
  SectionTableLoaderType,
  TableLoader,
} from '../page-data-table/table-loader';
import { TableError } from '../page-data-table/table-error';
import SectionTablePagination, {
  SectionPaginationType,
} from './section-table-pagination';
import styled from 'styled-components';
import { FlexRow } from '@make-software/cspr-design';

const BottomPaginationContainer = styled(FlexRow)<{
  hasLink?: boolean;
}>(({ hasLink }) => ({
  justifyContent: hasLink ? 'space-between' : 'flex-end',
  alignItems: 'center',
  height: 'fit-content',
  width: '100%',
}));

export type SectionTableProps<Entity> = {
  data: null | Entity[];
  loading: boolean;
  error?: string | null;
  paginationProps: PaginationProps;
  renderPaginatedData: (
    paginatedData: Entity[],
    renderProps: TableRenderProps
  ) => JSX.Element | JSX.Element[];
  paginationType?: SectionPaginationType;
  footerLink?: React.ReactElement;
};

const SectionTable = <Entity extends any>({
  data: tablePaginationData,
  loading,
  error,
  paginationProps,
  renderPaginatedData,
  footerLink,
  paginationType,
  ...props
}: SectionTableProps<Entity>) => {
  const renderBottomPagination = () => (
    <BottomPaginationContainer hasLink={!!footerLink}>
      {footerLink ? footerLink : null}
      <SectionTablePagination
        {...paginationProps!}
        paginationType={paginationType}
      />
    </BottomPaginationContainer>
  );

  const shouldShowPagination =
    paginationProps.itemCount > paginationProps.perPage;

  return (
    <Table
      renderData={(renderProps) =>
        tablePaginationData == null || loading ? (
          <TableLoader
            columnsLength={1}
            rowsLength={paginationProps.perPage}
            tableRowType={SectionTableLoaderType.SectionTableLine}
          />
        ) : error ? (
          <TableError columnsLength={1} height={200} />
        ) : (
          renderPaginatedData(tablePaginationData, renderProps)
        )
      }
      renderFooter={shouldShowPagination ? renderBottomPagination : undefined}
      {...props}
    />
  );
};

export default SectionTable;
